import { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { getArticleById, getRelatedArticles } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const article = getArticleById(id || '');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 md:pt-24">
          <div className="w-full max-w-4xl mx-auto px-4 md:px-6 py-20 text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-background-100 text-foreground-400">
              <i className="ri-error-warning-line text-3xl"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-sm text-foreground-600 mb-6">抱歉，您访问的文章不存在或已被移除。</p>
            <Link to="/" className="inline-flex px-6 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 cursor-pointer">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const relatedArticles = getRelatedArticles(article.id, article.category);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    image: article.image,
    publisher: {
      '@type': 'Organization',
      name: '17起草视频',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://17draft.com/article/${article.id}`,
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20 md:pt-24">
          <div className="w-full max-w-4xl mx-auto px-4 md:px-6 pb-16 md:pb-20">
            <div className="mb-6">
              <Breadcrumb
                items={[
                  { label: '首页', to: '/' },
                  {
                    label: article.categoryName,
                    to: `/category/${article.category}`,
                  },
                  { label: article.title },
                ]}
              />
            </div>

            <article>
              <div className="mb-6">
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  <span className="px-2.5 py-1 rounded-md bg-primary-100 text-primary-700 text-xs font-medium">
                    {article.categoryName}
                  </span>
                  {article.tags.map((tag) => (
                    <span key={tag} className="px-2.5 py-1 rounded-md bg-background-100 text-foreground-600 text-xs">
                      {tag}
                    </span>
                  ))}
                </div>
                <h1 className="text-xl md:text-3xl font-heading font-bold text-foreground-900 mb-4 leading-tight">
                  {article.title}
                </h1>
                <div className="flex flex-wrap items-center gap-3 text-xs md:text-sm text-foreground-500">
                  <div className="flex items-center gap-1.5">
                    <div className="w-6 h-6 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600">
                      <i className="ri-user-line text-xs"></i>
                    </div>
                    <span>{article.author}</span>
                  </div>
                  <span>·</span>
                  <div className="flex items-center gap-1.5">
                    <i className="ri-calendar-line"></i>
                    <span>{article.date}</span>
                  </div>
                  <span>·</span>
                  <div className="flex items-center gap-1.5">
                    <i className="ri-time-line"></i>
                    <span>{article.readTime}分钟阅读</span>
                  </div>
                </div>
              </div>

              <div className="mb-8 rounded-xl overflow-hidden">
                <img
                  src={article.image}
                  alt={article.title}
                  title={`${article.title} - 17起草视频`}
                  className="w-full h-[250px] md:h-[400px] object-cover"
                />
              </div>

              <div
                className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed"
                dangerouslySetInnerHTML={{
                  __html: article.content
                    .replace(/<h2>/g, '<h2 class="text-lg md:text-xl font-heading font-bold text-foreground-900 mt-8 mb-4">')
                    .replace(/<h3>/g, '<h3 class="text-base md:text-lg font-heading font-semibold text-foreground-900 mt-6 mb-3">')
                    .replace(/<p>/g, '<p class="text-sm md:text-base text-foreground-700 mb-4 leading-relaxed">')
                    .replace(/<ul>/g, '<ul class="list-disc pl-6 mb-4 space-y-1.5 text-sm md:text-base text-foreground-700">')
                    .replace(/<li>/g, '<li class="leading-relaxed">')
                    .replace(/<strong>/g, '<strong class="font-semibold text-foreground-900">'),
                }}
              />

              <div className="mt-10 pt-8 border-t border-background-200">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm text-foreground-500">标签：</span>
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1.5 rounded-full bg-background-100 text-foreground-600 text-xs cursor-pointer hover:bg-primary-100 hover:text-primary-700 transition-colors"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </article>

            {relatedArticles.length > 0 && (
              <section className="mt-12 md:mt-16">
                <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-5">
                  相关文章
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.id}
                      to={`/article/${ra.id}`}
                      className="group p-4 rounded-xl bg-background-100 border border-background-200/70 hover:border-primary-300 transition-all cursor-pointer"
                    >
                      <h3 className="text-sm font-heading font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {ra.title}
                      </h3>
                      <p className="text-xs text-foreground-600 mb-3 line-clamp-2 leading-relaxed">
                        {ra.excerpt}
                      </p>
                      <div className="text-xs text-foreground-500">
                        {ra.date} · {ra.readTime}分钟阅读
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}